import { z } from 'zod';
import { useProp } from '@docgen/sdk';
import type { BlockDef } from '../types.js';

export const stackBlock: BlockDef = {
  type: 'stack',
  name: 'Stack',
  description: 'Flex container that stacks its children vertically or in a row.',
  category: 'layout',
  icon: 'rows-3',
  propsSchema: z.object({ direction: z.enum(['column', 'row']).default('column') }),
  propFields: [
    { kind: 'select', name: 'direction', label: 'Direction', options: ['column', 'row'], default: 'column' },
  ],
  styleFields: ['gap', 'pad', 'bg', 'color', 'align', 'justify', 'radius', 'grow', 'width', 'mt', 'mb'],
  defaults: { props: { direction: 'column' }, children: [] },
  acceptsChildren: true,
  Component: function Stack({ attrs, style, children }) {
    const direction = useProp('direction') === 'row' ? 'row' : 'column';
    return (
      <div {...attrs} style={{ display: 'flex', flexDirection: direction, ...style }}>
        {children}
      </div>
    );
  },
};

export const columnsBlock: BlockDef = {
  type: 'columns',
  name: 'Columns',
  description: 'Side-by-side columns; each child becomes one equal column.',
  category: 'layout',
  icon: 'columns-2',
  propsSchema: z.object({}),
  propFields: [],
  styleFields: ['gap', 'pad', 'align', 'mt', 'mb'],
  defaults: { props: {}, style: { gap: 24 }, children: [] },
  acceptsChildren: true,
  Component: function Columns({ attrs, style, children }) {
    return (
      <div {...attrs} className="columns" style={{ display: 'flex', ...style }}>
        {children}
      </div>
    );
  },
};

export const gridBlock: BlockDef = {
  type: 'grid',
  name: 'Grid',
  description:
    'A symmetrical grid: equal-width columns and equal-height cells, however long the content. Put one block (usually a Card) in each cell.',
  category: 'layout',
  icon: 'grid-3x3',
  propsSchema: z.object({ columns: z.number().int().min(1).max(4).default(2) }),
  propFields: [{ kind: 'number', name: 'columns', label: 'Columns', default: 2 }],
  styleFields: ['gap', 'pad', 'align', 'mt', 'mb'],
  defaults: { props: { columns: 2 }, style: { gap: 16 }, children: [] },
  acceptsChildren: true,
  Component: function Grid({ attrs, style, children }) {
    const columns = Math.min(4, Math.max(1, Number(useProp('columns') ?? 2)));
    // grid-auto-rows:1fr (from .grid) equalises every row's height, and equal
    // fr columns keep the widths symmetrical regardless of cell content.
    return (
      <div {...attrs} className="grid" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, ...style }}>
        {children}
      </div>
    );
  },
};

/** Card fill/border options — the values ARE the recommended brand palette. */
const CARD_TONES = ['none', 'outline', 'mist', 'sky', 'royal', 'yellow', 'navy'] as const;

export const cardBlock: BlockDef = {
  type: 'card',
  name: 'Card',
  description:
    'A padded box for a grid cell or callout. `tone` sets a brand fill or border: outline (bordered, no fill), mist/sky/royal/yellow (soft brand tints), navy (solid, white text), none (plain). Holds a heading, text, an icon — anything.',
  category: 'layout',
  icon: 'square',
  propsSchema: z.object({ tone: z.enum(CARD_TONES).default('none') }),
  propFields: [{ kind: 'select', name: 'tone', label: 'Fill / border', options: [...CARD_TONES], default: 'none' }],
  styleFields: ['pad', 'radius', 'bg', 'mt', 'mb'],
  defaults: { props: { tone: 'mist' }, children: [] },
  acceptsChildren: true,
  Component: function Card({ attrs, style, children }) {
    const tone = String(useProp('tone') ?? 'none');
    const cls = CARD_TONES.includes(tone as (typeof CARD_TONES)[number]) ? tone : 'none';
    return (
      <div {...attrs} className={`card card--${cls}`} style={style}>
        {children}
      </div>
    );
  },
};

export const spacerBlock: BlockDef = {
  type: 'spacer',
  name: 'Spacer',
  description: 'Fixed vertical space, or flexible space that pushes content apart.',
  category: 'layout',
  icon: 'move-vertical',
  propsSchema: z.object({ size: z.number().default(24) }),
  propFields: [{ kind: 'number', name: 'size', label: 'Size (px)', default: 24 }],
  styleFields: ['grow'],
  defaults: { props: { size: 24 } },
  Component: function Spacer({ attrs, style }) {
    const size = Number(useProp('size') ?? 24);
    return <div {...attrs} style={{ height: style.flexGrow ? undefined : size, ...style }} aria-hidden />;
  },
};

export const dividerBlock: BlockDef = {
  type: 'divider',
  name: 'Divider',
  description: 'Thin horizontal line, or the brand rule bar.',
  category: 'layout',
  icon: 'minus',
  propsSchema: z.object({ variant: z.enum(['line', 'rule']).default('line') }),
  propFields: [
    { kind: 'select', name: 'variant', label: 'Variant', options: ['line', 'rule'], default: 'line' },
  ],
  styleFields: [],
  defaults: { props: { variant: 'line' } },
  Component: function Divider({ attrs, style }) {
    return useProp('variant') === 'rule' ? (
      <div {...attrs} className="rule" style={style} />
    ) : (
      <hr {...attrs} className="divider" style={style} />
    );
  },
};
